import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'

import { getDatadogSessionId } from '@/shared/external/datadog'
import { fixRequestBasePath } from '@/shared/lib/runtimeBasePath'

import {
  HTTP_ERROR_MESSAGE,
  HTTP_TIMEOUT_LIMIT,
  HTTP_USER_AGENT,
  isDev,
  isServer,
} from '../constants'
import { logHttpResponse } from '../logging'
import { handleRequestError } from './errorReport'
import { getPartnersAccessToken } from './partnersAuthToken'

const PARTNERS_PATH = '/api/partners'

const parseRequestBody = (raw: unknown): unknown => {
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

export interface IClientHttpOptions extends Omit<
  AxiosRequestConfig,
  'url' | 'method' | 'data'
> {
  body?: unknown
}

const axiosInstance = axios.create({
  timeout: HTTP_TIMEOUT_LIMIT,
  baseURL:
    process.env.NODE_ENV === 'production'
      ? ''
      : process.env.NEXT_PUBLIC_BASE_PATH,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

axiosInstance.interceptors.request.use(config => {
  if (isServer && !config.headers['User-Agent']) {
    config.headers['User-Agent'] = HTTP_USER_AGENT
  }

  // 서버 스팬에서 RUM 세션을 찾을 수 있게 세션 ID를 실어 보낸다 (tracer.cjs가 태그로 단다). 외부 도메인 노출을 막으려고 상대 경로(BFF)에만 붙인다.
  const rumSessionId =
    !isServer && config.url?.startsWith('/') && getDatadogSessionId()
  if (rumSessionId) {
    config.headers['x-rum-session-id'] = rumSessionId
  }

  if (
    !isServer &&
    !config.headers.Authorization &&
    config.url?.includes(PARTNERS_PATH)
  ) {
    const accessToken = getPartnersAccessToken()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
  }

  return fixRequestBasePath(config)
})

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    if (isDev) {
      logHttpResponse({
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        requestBody: parseRequestBody(response.config.data),
        data: response.data,
      })
    }

    return response.data
  },
  error => {
    if (isDev) {
      logHttpResponse({
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status: error.response?.status,
        requestBody: parseRequestBody(error.config?.data),
        data: error.response?.data,
      })
    }

    handleRequestError(error)

    if (!error.response) {
      return Promise.reject(new Error(HTTP_ERROR_MESSAGE.NETWORK))
    }

    return Promise.reject(error)
  }
)

const toAxiosConfig = ({ body: _body, ...config }: IClientHttpOptions = {}) =>
  config

export const clientHttp = {
  get: <T>(path: string, options?: IClientHttpOptions): Promise<T> =>
    axiosInstance.get<T, T>(path, toAxiosConfig(options)),

  post: <T>(path: string, options?: IClientHttpOptions): Promise<T> =>
    axiosInstance.post<T, T>(path, options?.body, toAxiosConfig(options)),

  put: <T>(path: string, options?: IClientHttpOptions): Promise<T> =>
    axiosInstance.put<T, T>(path, options?.body, toAxiosConfig(options)),

  patch: <T>(path: string, options?: IClientHttpOptions): Promise<T> =>
    axiosInstance.patch<T, T>(path, options?.body, toAxiosConfig(options)),

  delete: <T>(path: string, options?: IClientHttpOptions): Promise<T> =>
    axiosInstance.delete<T, T>(path, toAxiosConfig(options)),
}

// QA 패널(@/shared/ui/debug)이 이 클라이언트 요청에도 예외를 걸 수 있게 연다
export const clientHttpAxiosForQa = axiosInstance
